import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Search, X, Users, HeartPulse } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, titleCase, initials } from "@/lib/format";

function OrgDetail({ oid, onClose }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["sa-org", oid], queryFn: async () => (await api.get(`/superadmin/organisations/${oid}/detail`)).data });
  const updateMut = useMutation({
    mutationFn: async (body) => (await api.put(`/superadmin/organisations/${oid}`, body)).data,
    onSuccess: () => { toast.success("Organisation updated"); qc.invalidateQueries({ queryKey: ["sa-org", oid] }); qc.invalidateQueries({ queryKey: ["sa-orgs"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const org = data?.org;
  const staff = data?.staff || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/70 backdrop-blur-sm" onClick={onClose}>
      <div className="h-full w-full max-w-xl overflow-y-auto border-l border-white/10 bg-slate-900 p-6 text-slate-200" onClick={(e) => e.stopPropagation()} data-testid="sa-org-detail">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-white">Organisation</h2>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-white/5"><X className="h-5 w-5" /></button>
        </div>
        {isLoading ? <Loader /> : org ? (
          <div className="mt-5 space-y-5">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
              <p className="font-display text-lg font-bold text-white">{org.name}</p>
              <p className="font-mono text-xs text-slate-400">{org.osic} · {org.id}</p>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Currency</span><p className="text-white">{org.currency}</p></div>
                <div><span className="text-slate-500">Jurisdiction</span><p className="text-white">{org.jurisdiction}</p></div>
                <div><span className="text-slate-500">Created</span><p className="text-white">{fmtDate(org.created_at)}</p></div>
                <div><span className="text-slate-500">Plan</span><p className="uppercase text-white">{org.plan || "trial"}</p></div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
              <p className="mb-3 text-sm font-semibold text-white">Controls</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="text-xs text-slate-400">Status</label>
                  <Select defaultValue={org.status || "active"} onValueChange={(v) => updateMut.mutate({ status: v })}>
                    <SelectTrigger data-testid="sa-org-status" className="mt-1 border-white/10 bg-slate-800 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{["active", "suspended", "trial"].map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Plan</label>
                  <Select defaultValue={org.plan || "trial"} onValueChange={(v) => updateMut.mutate({ plan: v })}>
                    <SelectTrigger data-testid="sa-org-plan" className="mt-1 border-white/10 bg-slate-800 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent>{["trial", "standard", "enterprise"].map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
              <p className="mb-3 text-sm font-semibold text-white">Staff ({staff.length})</p>
              <div className="space-y-2">
                {staff.slice(0, 12).map((s) => (
                  <div key={s.user_id} className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-600 text-[11px] font-bold text-white">{initials(s.full_name)}</span>
                    <div className="min-w-0"><p className="truncate text-sm text-white">{s.full_name}</p><p className="truncate text-xs text-slate-400">{s.email} · {titleCase(s.role)}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : <p className="mt-5 text-slate-400">Not found.</p>}
      </div>
    </div>
  );
}

export function SAOrganisations() {
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const { data: orgs = [], isLoading } = useQuery({ queryKey: ["sa-orgs"], queryFn: async () => (await api.get("/superadmin/organisations")).data || [] });

  const filtered = orgs.filter((o) => !q || `${o.name} ${o.type} ${o.osic}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Organisations</h1>
          <p className="mt-1 text-sm text-slate-400">{orgs.length} tenants on the platform</p>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input data-testid="sa-org-search" placeholder="Search organisations…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64 border-white/10 bg-white/5 pl-9 text-white placeholder:text-slate-500" />
        </div>
      </div>

      {isLoading ? <Loader full /> : (
        <div className="mt-6 overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3">Organisation</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Users</th><th className="px-4 py-3">Patients</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((o, i) => (
                  <tr key={o.id} data-testid={`sa-org-row-${i}`} onClick={() => setSelected(o.id)} className="cursor-pointer text-sm text-slate-300 transition-colors hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-600/20 text-teal-400"><Building2 className="h-4 w-4" /></span>
                        <div><p className="font-medium text-white">{o.name}</p><p className="font-mono text-xs text-slate-500">{o.osic}</p></div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{titleCase(o.type)}</td>
                    <td className="px-4 py-3"><span className="uppercase">{o.plan || "trial"}</span></td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-slate-500" /> {o.user_count ?? 0}</span></td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1"><HeartPulse className="h-3.5 w-3.5 text-slate-500" /> {o.patient_count ?? 0}</span></td>
                    <td className="px-4 py-3"><StatusBadge value={o.status || "active"} /></td>
                    <td className="px-4 py-3">{fmtDate(o.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && <OrgDetail oid={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
